import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { contract, formatPlaceName, getNoIndexValue, getPlaceNameForSeoDescription, placeTypeLegacyMapper, } from '@aviv_price-pages/brand';
import { Head } from '@aviv-unified-frontend/runtime/head';
import { useLocale } from '@aviv-unified-frontend/runtime/i18n';
import { useLocation, useSearchParams } from '@aviv-unified-frontend/runtime/router';
import { formatCurrency } from '@/helpers/format/formatCurrency';
import { cleanUrl } from '@/helpers/format/formatUrl';
import { getAssetsPath } from '@/helpers/getAssetsPath';
import { usePageTitle } from '@/hooks/usePageTitle/usePageTitle';
import { useShellTranslation } from '@/hooks/useShellTranslation/useShellTranslation';
export const Seo = ({ pricePageContext, i18nkey, isStreetListPage }) => {
    const { t } = useShellTranslation();
    const { language } = useLocale();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const placeType = pricePageContext.place?.typeKey;
    const isUrlFallback = searchParams.get('urlfallback');
    const noindex = isUrlFallback || getNoIndexValue(pricePageContext.place, undefined, isStreetListPage);
    const dateObj = useMemo(() => {
        const date = new Date();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        return { month, year };
    }, []);
    const origin = contract.brandDomain
        ? `https://${contract.brandDomain}`
        : 'http://localhost:3000';
    const url = `${origin}${pathname}`;
    const seoKey = i18nkey || placeTypeLegacyMapper(placeType);
    const formatedSeoTitlePlaceName = formatPlaceName(pricePageContext.place, {
        withProvince: pricePageContext.isDuplicatePlaceName,
    });
    const formatedSeoDescriptionPlaceName = getPlaceNameForSeoDescription(pricePageContext?.place);
    const getPlaceName = (defaultValue) => {
        return t(`place_name.${seoKey}`) === `place_name.${seoKey}`
            ? defaultValue
            : t(`place_name.${seoKey}`);
    };
    const seoParams = {
        title: t(`head.title.${seoKey}`, {
            replace: {
                place: getPlaceName(formatedSeoTitlePlaceName),
                postal_code: pricePageContext?.place?.postalCode || '',
                date: `${dateObj.month}/${dateObj.year}`,
                month: dateObj.month,
                year: dateObj.year,
            },
            unsafe_escapeValue: false,
        }),
        description: t(`head.meta_description.${seoKey}`, {
            replace: {
                place: getPlaceName(formatedSeoDescriptionPlaceName),
                postal_code: pricePageContext?.place?.postalCode || '',
                house_price: formatCurrency(pricePageContext.prices?.sell?.house?.average || 0, language),
                apartment_price: formatCurrency(pricePageContext.prices?.sell?.apartment?.average || 0, language),
                hybrid_price: formatCurrency(pricePageContext.prices?.sell?.hybrid?.average || 0, language),
            },
            unsafe_escapeValue: false,
        }),
    };
    const title = usePageTitle(pricePageContext.place, pricePageContext.isDuplicatePlaceName);
    const robotsContent = noindex ? 'noindex, follow' : 'index, follow';
    const ldJson = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: title,
        operatingSystem: 'Web-based',
        applicationCategory: 'Finance',
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.2',
            ratingCount: '1749',
        },
        offers: {
            '@type': 'Offer',
            price: '0.00',
            priceCurrency: 'EUR',
        },
    });
    return (_jsxs(Head, { children: [contract.withPageLdJson && !isStreetListPage && (_jsx("script", { type: "application/ld+json", children: ldJson })), _jsx("title", { children: seoParams.title }), _jsx("link", { rel: "canonical", href: cleanUrl(url) }), _jsx("meta", { content: seoParams.description, name: "description" }), _jsx("meta", { name: "robots", content: robotsContent }), _jsx("meta", { property: "og:tile", content: seoParams.title }), _jsx("meta", { property: "og:description", content: seoParams.description }), _jsx("meta", { property: "og:type", content: "website" }), _jsx("meta", { property: "og:url", content: cleanUrl(url) }), _jsx("meta", { property: "og:image", content: `${getAssetsPath('opengraph.png')}` }), _jsx("meta", { property: "og:image:type", content: "image/png" }), _jsx("meta", { property: "og:image:width", content: "1200" }), _jsx("meta", { property: "og:image:height", content: "600" }), _jsx("meta", { property: "og:image.alt", content: t('head.og_image_alt') })] }));
};
